import { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorMode,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Tooltip,
  Flex,
  Icon,
  SimpleGrid,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faCog,
  faSync,
  faChartLine,
  faNetworkWired,
  faHammer,
  faBolt,
  faExchangeAlt,
  faArrowUp,
  faArrowDown,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faRobot,
  faQuoteLeft,
  faBookOpen,
  faGlobe,
  faShareNodes,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import ApexCharts from 'apexcharts';
import { shouldUseAI, getConfiguredCount, getEstimatedTokens, getActiveProvider, getProviderKey, isConfigValid, NO_AI, AI_PROVIDERS, AUTO_MODEL } from '../../utils/ai/aiProviders';
import { formatLargeNumber } from '../../utils/number.utils';
import { generateStructuredInsight, getMetricsSummary } from '../../utils/ai/aiService';
import { generateLocalInsight } from '../../utils/ai/localInsights';
import { shareInsightCard } from '../../utils/ai/shareCard';
import { fmt, clean } from '../../utils/data/canonicalMetrics';
import { AIConfigModal } from './AIConfigModal';
import { getRandomSatoshiQuote } from '../../utils/satoshiQuotes';
import { useGlossary } from '../../contexts/GlossaryContext';

const MotionBox = motion(Box);

// Intro modal localStorage
const INTRO_KEY = 'ai_insights_intro_seen';
const INTRO_EXPIRY = 60 * 60 * 1000; // 1 hour

const shouldShowIntro = () => {
  try {
    const saved = localStorage.getItem(INTRO_KEY);
    if (!saved) return true;
    return Date.now() - parseInt(saved, 10) > INTRO_EXPIRY;
  } catch { return true; }
};

// Category configuration
const CATEGORIES = {
  market: { label: 'Market', icon: faChartLine, color: 'green' },
  network: { label: 'Network', icon: faNetworkWired, color: 'blue' },
  mining: { label: 'Mining', icon: faHammer, color: 'orange' },
  lightning: { label: 'Lightning', icon: faBolt, color: 'yellow' },
  mempool: { label: 'Mempool', icon: faExchangeAlt, color: 'cyan' }
};

const ALL_CATEGORIES = Object.keys(CATEGORIES);

// ---- Insight cache (module scope — pure of component state) ----
const CACHE_KEY = 'ai_insights_cache_v2';
const CACHE_DURATION = 30 * 60 * 1000;
const CACHE_MAX_ENTRIES = 10;
const CATEGORIES_KEY = 'ai_insights_selected_categories';

// Share-as-image feature — hidden for now; flip to true to re-enable.
const SHOW_SHARE = false;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Staged "thinking" log for the local Timechain Stats AI engine — so it visibly
// works like the AI providers (~5s) instead of returning instantly.
const LOCAL_THINKING_STEPS = [
  { step: 'reading', detail: 'Reading live network metrics...' },
  { step: 'benchmarks', detail: 'Benchmarking hashrate, fees, mempool...' },
  { step: 'trends', detail: 'Detecting trends and anomalies...' },
  { step: 'generating', detail: 'Querying Timechain Stats AI...' },
  { step: 'building', detail: 'Building dashboard...' }
];

const makeCacheKey = (blockHeight, categories, currency) => {
  const cats = [...categories].sort().join(',') || 'none';
  return `${blockHeight ?? 'no-block'}|${cats}|${currency || 'usd'}`;
};

const loadCacheMap = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v?.timestamp && now - v.timestamp < CACHE_DURATION)
    );
  } catch (e) {
    console.warn('[AIInsights] Failed to load cache:', e.message);
    return {};
  }
};

const saveCacheEntry = (key, entry) => {
  try {
    const map = loadCacheMap();
    map[key] = entry;
    const trimmed = Object.fromEntries(
      Object.entries(map)
        .sort(([, a], [, b]) => (b?.timestamp || 0) - (a?.timestamp || 0))
        .slice(0, CACHE_MAX_ENTRIES)
    );
    localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('[AIInsights] Failed to save cache:', e.message);
  }
};

const loadSelectedCategories = () => {
  try {
    const raw = localStorage.getItem(CATEGORIES_KEY);
    if (!raw) return ALL_CATEGORIES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(k => ALL_CATEGORIES.includes(k))) {
      return parsed;
    }
  } catch (e) {
    console.warn('[AIInsights] Failed to load categories:', e.message);
  }
  return ALL_CATEGORIES;
};

// Format object keys for display (e.g., "MINOR_ISSUES" -> "MINOR ISSUES")
const formatKeyForDisplay = (key) => {
  if (!key || typeof key !== 'string') return key;
  return key.replace(/_/g, ' ');
};

// Alert icon and color mapping
const getAlertConfig = (type) => {
  switch (type) {
    case 'warning': return { icon: faExclamationTriangle, color: 'orange' };
    case 'success': return { icon: faCheckCircle, color: 'green' };
    default: return { icon: faInfoCircle, color: 'blue' };
  }
};

// Format metric value - preserve already-formatted values
const formatMetricValue = (value) => {
  if (!value) return 'N/A';
  if (typeof value !== 'string') return String(value);
  // Already has formatting (currency, percent, BTC, abbreviations) - keep as-is
  if (/[$%]|BTC|sat|[TBMK]\s*$|^\d+\.\d{8}/i.test(value)) return value;
  // Has commas = already formatted (e.g. block height "878,543") - keep as-is
  if (value.includes(',')) return value;
  // Plain large number without formatting - apply abbreviations
  const num = parseFloat(value);
  if (!isNaN(num) && num >= 1000000) {
    return formatLargeNumber(num, 2);
  }
  return value;
};

// Theme colors from theme.js
const THEME_COLORS = {
  orange: { 100: '#fae1b1', 300: '#ffc451', 500: '#f7931a', 700: '#be6500' },
  blue: { 100: '#dfedff', 300: '#b3d4fc', 400: '#4a96d4', 500: '#0d579b', 700: '#002f6c' },
  green: { 300: '#a5d6a7', 500: '#329239', 700: '#1b5e20' },
  red: { 300: '#ef9a9a', 500: '#c62828', 700: '#8e0000' },
  purple: { 300: '#ba68c8', 500: '#8B00FF', 700: '#6B238E' },
  gray: { 100: '#e0e0e0', 200: '#999999', 300: '#666666', 500: '#4d4d4d', 700: '#777777' },
  primary: { 300: '#272727', 500: '#1d1d1d', 700: '#121212' }
};


// Semantic status tones — every status badge maps to ONE of three meanings so
// color is never ambiguous:
//   positive (healthy/profitable/strong/growing/clear/bullish) → green
//   negative (weak/busy/congested/stressed/declining/bearish)  → red
//   neutral  (normal/stable/consolidating/…)                    → gray
const POSITIVE_STATUS = new Set(['strong', 'healthy', 'profitable', 'growing', 'clear', 'good', 'bullish', 'expanding', 'stable']);
const NEGATIVE_STATUS = new Set(['weak', 'stressed', 'challenging', 'declining', 'congested', 'busy', 'bearish', 'critical']);

const statusTone = (value) => {
  const v = String(value || '').toLowerCase().trim();
  if (POSITIVE_STATUS.has(v)) return 'positive';
  if (NEGATIVE_STATUS.has(v)) return 'negative';
  return 'neutral';
};

// Flat hex per tone (used by the shareable insight card)
const TONE_HEX = {
  positive: THEME_COLORS.green[500],
  negative: THEME_COLORS.red[500],
  neutral: THEME_COLORS.gray[200]
};
const statusHex = (value) => TONE_HEX[statusTone(value)];

// Map a metric/benchmark/anomaly key to the top-level categories it belongs to, so a
// pre-run category filter can tailor the WHOLE dashboard, not just the section cards.
const KEY_CATEGORIES = {
  hashrate: ['network', 'mining'],
  difficulty: ['network', 'mining'],
  blocktime: ['network'],
  nodes: ['network'],
  nodecount: ['network'],
  blockheight: ['network'],
  txs24h: ['network'],
  transactions: ['network'],
  activeaddrs: ['network', 'market'],
  fees: ['mempool', 'mining'],
  fastestfee: ['mempool', 'mining'],
  mempool: ['mempool'],
  pricechange: ['market'],
  price: ['market'],
  volatility: ['market'],
  halving: ['mining'],
  lnnodes: ['lightning'],
  lnbtccapacity: ['lightning']
};
const normKey = (k) => String(k || '').toLowerCase().replace(/[^a-z0-9]/g, '');
// A category filter is "active" when it's a real subset. Empty/null = show everything.
const catActive = (cat, active) => !active || active.length === 0 || active.includes(cat);
const keyInCategories = (key, active) => {
  if (!active || active.length === 0) return true;
  const cats = KEY_CATEGORIES[normKey(key)];
  if (!cats) return true; // unknown key → don't hide data we can't classify
  return cats.some((c) => active.includes(c));
};

// Pill styling per tone + color mode: muted text + a soft tint of the same color.
const toneStyles = (tone, colorMode) => {
  const dark = colorMode === 'dark';
  if (tone === 'positive') {
    return { color: dark ? THEME_COLORS.green[300] : THEME_COLORS.green[500], bg: dark ? 'rgba(50,146,57,0.20)' : 'rgba(50,146,57,0.12)' };
  }
  if (tone === 'negative') {
    return { color: dark ? THEME_COLORS.red[300] : THEME_COLORS.red[500], bg: dark ? 'rgba(198,40,40,0.22)' : 'rgba(198,40,40,0.10)' };
  }
  return { color: dark ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300], bg: dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' };
};

// Status pill — dot + label on a soft tinted background. One consistent shape everywhere.
const StatusBadge = memo(({ status, colorMode, size = 'md', tone }) => {
  if (!status) return null;
  const s = toneStyles(tone || statusTone(status), colorMode);
  const sm = size === 'sm';
  return (
    <HStack
      as="span"
      display="inline-flex"
      spacing={sm ? 1 : 1.5}
      bg={s.bg}
      px={sm ? 1.5 : 2}
      py={sm ? '2px' : '3px'}
      borderRadius="full"
      align="center"
      flexShrink={0}
    >
      <Box w={sm ? '5px' : '6px'} h={sm ? '5px' : '6px'} borderRadius="full" bg={s.color} flexShrink={0} />
      <Text fontSize={sm ? '9px' : '11px'} fontWeight="700" letterSpacing="0.04em" textTransform="uppercase" color={s.color} lineHeight="1.5" whiteSpace="nowrap">
        {status}
      </Text>
    </HStack>
  );
});
StatusBadge.displayName = 'StatusBadge';

// Metric Card component - matches app's SingleDataValue styling
const MetricCard = memo(({ metric, colorMode }) => {
  if (!metric) return null;
  const { label, value, change, significance } = metric;
  const hasChange = change !== null && change !== undefined && Number.isFinite(change);
  const isPositive = hasChange && change >= 0;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const valueColor = colorMode === 'dark' ? '#ffffff' : '#121212';

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
      borderWidth={significance === 'high' ? '1px' : '0'}
      borderColor={significance === 'high' ? (colorMode === 'dark' ? THEME_COLORS.orange[500] : THEME_COLORS.blue[500]) : 'transparent'}
      textAlign="center"
    >
      <Text
        fontSize="13px"
        color={labelColor}
        fontWeight="700"
        textTransform="uppercase"
        lineHeight="1"
        mb={2}
      >
        {label}
      </Text>
      <HStack spacing={1} justify="center" align="center">
        <Text fontSize="20px" fontWeight="700" lineHeight="1" color={valueColor}>
          {formatMetricValue(value)}
        </Text>
        {hasChange && (
          <HStack spacing={0.5} color={isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>
            <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" />
            <Text fontSize="12px" fontWeight="700">
              {clean(Math.abs(change))}%
            </Text>
          </HStack>
        )}
      </HStack>
    </Flex>
  );
});
MetricCard.displayName = 'MetricCard';

// Progress Bar component - uses theme colors
const InsightProgressBar = memo(({ label, progress, detail, colorMode }) => {
  const progressNum = parseFloat(progress) || 0;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const accentColor = THEME_COLORS.orange[500];

  return (
    <Box bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'} p={3} borderRadius="3px">
      <HStack justify="space-between" mb={2}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor} lineHeight="1">{label}</Text>
        <Text fontSize="14px" fontWeight="700" color={accentColor}>
          {clean(progressNum)}%
        </Text>
      </HStack>
      <Box
        h="6px"
        bg={colorMode === 'dark' ? THEME_COLORS.gray[500] : THEME_COLORS.gray[100]}
        borderRadius="3px"
        overflow="hidden"
      >
        <Box
          h="100%"
          w={`${Math.min(progressNum, 100)}%`}
          bg={accentColor}
          borderRadius="3px"
          transition="width 0.3s ease"
        />
      </Box>
      {detail && <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} mt={2}>{detail}</Text>}
    </Box>
  );
});
InsightProgressBar.displayName = 'InsightProgressBar';

// Alert Badge component - uses theme colors
const AlertBadge = memo(({ alert, colorMode }) => {
  if (!alert?.message) return null;
  const config = getAlertConfig(alert.type);
  const alertColors = {
    warning: { bg: colorMode === 'dark' ? '#3d2800' : THEME_COLORS.orange[100], border: THEME_COLORS.orange[500], icon: THEME_COLORS.orange[500] },
    success: { bg: colorMode === 'dark' ? '#0d2610' : '#e8f5e9', border: THEME_COLORS.green[500], icon: THEME_COLORS.green[500] },
    info: { bg: colorMode === 'dark' ? '#002f6c' : THEME_COLORS.gray[100], border: THEME_COLORS.blue[400], icon: THEME_COLORS.blue[400] }
  };
  const colors = alertColors[alert.type] || alertColors.info;

  return (
    <HStack
      spacing={2}
      py={2}
      px={3}
      bg={colors.bg}
      borderRadius="3px"
      width="fit-content"
    >
      <FontAwesomeIcon icon={config.icon} color={colors.icon} size="sm" />
      <Text fontSize="12px" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{alert.message}</Text>
    </HStack>
  );
});
AlertBadge.displayName = 'AlertBadge';

// Section Status component - uses theme colors
const SectionStatus = memo(({ category, status, highlight, colorMode }) => {
  const cfg = CATEGORIES[category];
  if (!cfg) return null;

  const categoryColors = {
    market: THEME_COLORS.green[500],
    network: THEME_COLORS.blue[400],
    mining: THEME_COLORS.orange[500],
    lightning: THEME_COLORS.orange[300],
    mempool: THEME_COLORS.blue[400]
  };

  const accentColor = categoryColors[category] || THEME_COLORS.gray[200];

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
      h="100%"
    >
      <HStack justify="space-between" mb={highlight ? 2 : 0}>
        <HStack spacing={2}>
          <Icon as={FontAwesomeIcon} icon={cfg.icon} color={accentColor} boxSize="18px" />
          <Text fontSize="16px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{cfg.label}</Text>
        </HStack>
        <StatusBadge status={status} colorMode={colorMode} size="sm" />
      </HStack>
      {highlight && (
        <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} noOfLines={2} lineHeight="1.3">
          {highlight}
        </Text>
      )}
    </Box>
  );
});
SectionStatus.displayName = 'SectionStatus';

// Analytics Panel - uses theme colors, compact grid layout
const AnalyticsPanel = memo(({ analytics, colorMode, categories }) => {
  if (!analytics) return null;

  const active = Array.isArray(categories) && categories.length > 0 ? categories : null;
  const { trends, anomalies, correlations, benchmarks, marketCondition, flags } = analytics;
  // Tailor each analytics card to the selected filter set
  const benchmarkEntries = benchmarks ? Object.entries(benchmarks).filter(([key, b]) => b && keyInCategories(key, active)) : [];
  const anomalyItems = (anomalies || []).filter(a => keyInCategories(a?.metric, active));
  const showPriceHash = correlations?.priceHashrate?.value != null && (catActive('market', active) || catActive('network', active));
  const showFeeMempool = correlations?.feeMempool?.value != null && (catActive('mempool', active) || catActive('mining', active));
  const bgColor = colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff';
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const textColor = colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300];
  const textPrimary = colorMode === 'dark' ? '#ffffff' : '#121212';

  return (
    <VStack spacing={2} align="stretch">
      {/* Advanced Analytics: Market, Network, Trend, Benchmarks, Anomalies, Correlations */}
      <Flex flexWrap="wrap" gap={2}>
        {marketCondition && catActive('market', active) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Market</Text>
              <StatusBadge status={marketCondition.sentiment} colorMode={colorMode} size="sm" />
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="12px" color={textColor} lineHeight="1.3">
              {marketCondition.summary.split(/[;,]/).map((part, i) => {
                const trimmed = part.trim();
                return trimmed ? <Text key={i}>{trimmed}</Text> : null;
              })}
            </VStack>
          </Box>
        )}

        {flags?.networkHealth && catActive('network', active) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Network</Text>
              <StatusBadge status={formatKeyForDisplay(flags.networkHealth)} tone={statusTone(flags.networkHealth)} colorMode={colorMode} size="sm" />
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="12px" color={textColor} lineHeight="1.3">
              {benchmarks?.hashrate?.context && (
                <Text>{benchmarks.hashrate.context}</Text>
              )}
              {benchmarks?.blockTime?.context && (
                <Text>{benchmarks.blockTime.context}</Text>
              )}
            </VStack>
          </Box>
        )}

        {trends?.price && catActive('market', active) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Trend</Text>
              <StatusBadge
                status={trends.price.strength !== 'NEUTRAL'
                  ? `${trends.price.strength} ${trends.price.direction}`
                  : trends.price.direction}
                tone={statusTone(trends.price.direction)}
                colorMode={colorMode}
                size="sm"
              />
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="14px" color={textColor} lineHeight="1.3">
              <HStack gap={2}>
                {trends.movingAverages?.price?.ma7 && <Text>7d: <Text as="span" fontWeight="700" color={textPrimary}>${clean(trends.movingAverages.price.ma7 / 1000)}k</Text></Text>}
                {trends.movingAverages?.price?.ma30 && <Text>30d: <Text as="span" fontWeight="700" color={textPrimary}>${clean(trends.movingAverages.price.ma30 / 1000)}k</Text></Text>}
              </HStack>
              {trends.movingAverages?.price?.currentVsMa7 != null && (
                <Text>vs 7d: <Text as="span" fontWeight="700" color={trends.movingAverages.price.currentVsMa7 >= 0 ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>{trends.movingAverages.price.currentVsMa7 >= 0 ? '+' : ''}{clean(trends.movingAverages.price.currentVsMa7)}%</Text></Text>
              )}
            </VStack>
          </Box>
        )}

        {benchmarkEntries.length > 0 && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor} mb={1}>Benchmarks</Text>
            <Flex flexWrap="wrap" gap={1}>
              {benchmarkEntries.map(([key, b]) => {
                if (!b) return null;
                const keyLower = key.toLowerCase();
                const isSecurityMetric = ['hashrate', 'difficulty', 'nodes', 'nodecount'].includes(keyLower);
                const levelColorHex = (() => {
                  if (['CONGESTED', 'SLOW', 'EXTREME'].includes(b.level)) return THEME_COLORS.red[300];
                  if (['HIGH'].includes(b.level)) {
                    return isSecurityMetric ? THEME_COLORS.green[500] : THEME_COLORS.red[300];
                  }
                  if (['LOW', 'LIGHT'].includes(b.level)) {
                    return isSecurityMetric ? THEME_COLORS.orange[500] : THEME_COLORS.green[500];
                  }
                  return THEME_COLORS.green[500];
                })();
                return (
                  <Tooltip key={key} label={b.context} placement="top">
                    <Text fontSize="9px" fontWeight="700" color={levelColorHex} cursor="help" bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} px={1} py={0.5} borderRadius="3px" textTransform="uppercase">
                      {formatKeyForDisplay(key)}: {formatKeyForDisplay(b.level)}
                    </Text>
                  </Tooltip>
                );
              })}
            </Flex>
          </Box>
        )}

        {anomalyItems.length > 0 && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack mb={1}>
              <FontAwesomeIcon icon={faExclamationTriangle} color={THEME_COLORS.orange[500]} size="xs" />
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Anomalies ({anomalyItems.length})</Text>
            </HStack>
            <Flex flexWrap="wrap" gap={1}>
              {anomalyItems.slice(0, 3).map((anomaly, idx) => (
                <Tooltip key={idx} label={anomaly.message} placement="top">
                  <Text fontSize="9px" fontWeight="700" cursor="help" color={anomaly.severity === 'CRITICAL' ? THEME_COLORS.red[300] : THEME_COLORS.orange[500]} bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} px={1} py={0.5} borderRadius="3px" textTransform="uppercase">
                    {formatKeyForDisplay(anomaly.metric)}: {formatKeyForDisplay(anomaly.direction || anomaly.severity)}
                  </Text>
                </Tooltip>
              ))}
            </Flex>
          </Box>
        )}

        {(showPriceHash || showFeeMempool) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor} mb={1}>Correlations</Text>
            <Flex fontSize="13px" color={textColor} gap={4} flexWrap="wrap">
              {showPriceHash && (
                <Tooltip label={correlations.priceHashrate.interpretation} placement="top">
                  <Text cursor="help">Price-Hash: <Text as="span" fontWeight="700" color={correlations.priceHashrate.value > 0.5 ? THEME_COLORS.green[500] : THEME_COLORS.gray[200]}>{clean(correlations.priceHashrate.value, 2)}</Text></Text>
                </Tooltip>
              )}
              {showFeeMempool && (
                <Tooltip label={correlations.feeMempool.interpretation} placement="top">
                  <Text cursor="help">Fee-Mempool: <Text as="span" fontWeight="700" color={correlations.feeMempool.value > 0.5 ? THEME_COLORS.green[500] : THEME_COLORS.gray[200]}>{clean(correlations.feeMempool.value, 2)}</Text></Text>
                </Tooltip>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </VStack>
  );
});
AnalyticsPanel.displayName = 'AnalyticsPanel';

// Satoshi Quote Flair - uses theme colors
const SatoshiQuoteFlair = memo(({ colorMode }) => {
  const [quoteObj] = useState(() => getRandomSatoshiQuote());

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
    >
      <HStack spacing={2} mb={2}>
        <FontAwesomeIcon icon={faQuoteLeft} color={THEME_COLORS.orange[500]} size="sm" />
        <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase">
          Satoshi Nakamoto
        </Text>
      </HStack>
      <Text fontSize="14px" fontStyle="italic" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} lineHeight="1.5">
        {quoteObj.quote}
      </Text>
    </Box>
  );
});
SatoshiQuoteFlair.displayName = 'SatoshiQuoteFlair';

// BTCLexicon Term Flair - uses theme colors
const LexiconTermFlair = memo(({ colorMode }) => {
  const { glossaryData, loadGlossary, isLoaded } = useGlossary();
  const [term, setTerm] = useState(null);

  useEffect(() => {
    if (!isLoaded) {
      loadGlossary();
    }
  }, [isLoaded, loadGlossary]);

  useEffect(() => {
    if (glossaryData && typeof glossaryData === 'object') {
      const allTerms = Object.entries(glossaryData)
        .filter(([category]) => category !== 'version')
        .flatMap(([, terms]) => Object.values(terms))
        .filter(t => t && t.title && t.definition);

      if (allTerms.length > 0) {
        const randomTerm = allTerms[Math.floor(Math.random() * allTerms.length)];
        setTerm(randomTerm);
      }
    }
  }, [glossaryData]);

  if (!term) return null;

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
    >
      <HStack spacing={2} mb={2}>
        <FontAwesomeIcon icon={faBookOpen} color={THEME_COLORS.orange[500]} size="sm" />
        <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase">
          BTC Lexicon
        </Text>
      </HStack>
      <Text fontSize="15px" fontWeight="700" mb={1} color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{term.title}</Text>
      <Text fontSize="14px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} lineHeight="1.5">
        {term.definition}
      </Text>
    </Box>
  );
});
LexiconTermFlair.displayName = 'LexiconTermFlair';

// Mini Price Chart - uses theme colors
const MiniPriceChart = memo(({ data, colorMode, compact = false, canonicalChange30d }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const chartHeight = compact ? 60 : 100;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];

  useEffect(() => {
    if (!data?.ts15?.price?.values || !chartRef.current) return;

    const priceData = data.ts15.price.values;
    const recentData = priceData.slice(-30).map(p => ({
      x: new Date(p.x * 1000),
      y: p.y
    }));

    if (recentData.length < 2) return;

    const firstPrice = recentData[0].y;
    const lastPrice = recentData[recentData.length - 1].y;
    const isPositive = lastPrice >= firstPrice;
    const chartColor = isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[500];

    const options = {
      chart: {
        type: 'area',
        height: chartHeight,
        sparkline: { enabled: true },
        animations: { enabled: false }
      },
      series: [{ name: 'Price', data: recentData }],
      stroke: { curve: 'smooth', width: compact ? 1.5 : 2 },
      colors: [chartColor],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      tooltip: {
        enabled: !compact,
        theme: colorMode,
        x: { format: 'MMM dd' },
        y: { formatter: (val) => `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` }
      }
    };

    if (chartInstance.current) {
      chartInstance.current.updateOptions(options, false, false, false);
    } else {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, colorMode, chartHeight, compact]);

  if (!data?.ts15?.price?.values) return null;

  const priceData = data.ts15.price.values;
  const recentData = priceData.slice(-30);
  if (recentData.length < 2) return null;

  const firstPrice = recentData[0].y;
  const lastPrice = recentData[recentData.length - 1].y;
  // Use canonical metrics as single source of truth for 30d change, fall back to chart calculation
  const change = canonicalChange30d != null ? canonicalChange30d : ((lastPrice - firstPrice) / firstPrice) * 100;
  const isPositive = change >= 0;
  const changeColor = isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[500];

  if (compact) {
    return (
      <VStack spacing={1} align="stretch" h="100%">
        <HStack justify="space-between">
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor}>30d Trend</Text>
          <HStack spacing={1}>
            <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" color={changeColor} />
            <Text fontSize="12px" fontWeight="700" color={changeColor}>{clean(Math.abs(change))}%</Text>
          </HStack>
        </HStack>
        <Box ref={chartRef} height={`${chartHeight}px`} flex={1} />
      </VStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor}>30-Day Price Trend</Text>
        <HStack spacing={1}>
          <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" color={changeColor} />
          <Text fontSize="14px" fontWeight="700" color={changeColor}>{clean(Math.abs(change))}%</Text>
        </HStack>
      </HStack>
      <Box ref={chartRef} height={`${chartHeight}px`} />
    </VStack>
  );
});
MiniPriceChart.displayName = 'MiniPriceChart';

// Unified Dashboard Panel - uses theme colors
const UnifiedDashboard = memo(({ insight, colorMode, data, bitcoinPrice, metrics: m, categories }) => {
  if (!insight) return null;

  const { headline, overallSentiment, sentiment, marketScore, sections, alerts, insights: textInsights, outlook } = insight;
  const displaySentiment = sentiment || overallSentiment;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const bgCard = colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff';
  const textPrimary = colorMode === 'dark' ? '#ffffff' : '#121212';

  const scoreColor = marketScore >= 7 ? THEME_COLORS.green[500] : marketScore >= 4 ? THEME_COLORS.orange[500] : THEME_COLORS.red[300];

  // Calculate ATH distance from real data
  const ath = data?.s06?.bitcoin?.market_data?.ath?.usd;
  const price = parseFloat(bitcoinPrice);
  const athDistance = (ath && price) ? ((price - ath) / ath * 100) : null;

  // Fix incorrect ATH references in AI-generated text (post-processing guard)
  const fixAthReferences = (text) => {
    if (!text || athDistance == null) return text;
    const realPct = Math.abs(athDistance).toFixed(0);
    return text.replace(
      /(\d{1,3}(?:\.\d+)?)\s*%\s*(?:from|below|off|under)\s*(?:ATH|all[- ]time high|the all[- ]time (?:high|peak))/gi,
      `${realPct}% below ATH`
    );
  };

  // Replace ALL tildes with "about" in analysis/watch text — eliminates garbled ~1.~1 forever
  const replaceTildes = (text) => {
    if (!text) return text;
    return text
      // Fix garbled tildes mid-number FIRST: "~1.~1" → "1.1"
      .replace(/~(\d+)\.~(\d+)/g, '$1.$2')
      // Fix any tilde-between-digits: "~1~1" → "11"
      .replace(/~(\d[\d.]*)~(\d)/g, '$1$2')
      // Replace "~number" with "about number" (e.g. "~1.1 ZH/s" → "about 1.1 ZH/s")
      .replace(/~(\d)/g, 'about $1');
  };

  // Capitalize first letter of a string
  const capitalizeFirst = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Replace em/en dashes (a common LLM tell) with a clean sentence break: "X — y" → "X. Y".
  // Only targets em/en/double-hyphen with surrounding spaces, so ranges like "1 - 300" are untouched.
  const fixDashes = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      // dangling dash before punctuation or end of string → just drop it
      .replace(/\s*[—–]+\s*(?=[.,;:!?)\]]|$)/g, '')
      // dash joining two clauses → sentence break with the next word capitalized
      .replace(/\s*(?:—|–|--)\s*(\w)/g, (_, c) => `. ${c.toUpperCase()}`);
  };

  // Compose all AI text post-processors — applied to Analysis and Watch blurbs
  const fixAIText = (text) => capitalizeFirst(fixDashes(replaceTildes(fixAthReferences(text))));

  // Enforce length limits client-side (LLMs don't count words reliably).
  const clampWords = (text, max) => {
    if (!text || typeof text !== 'string') return text;
    const words = text.trim().split(/\s+/);
    if (words.length <= max) return text.trim();
    return words.slice(0, max).join(' ').replace(/[,;:—-]+$/, '') + '…';
  };
  // Headline: price + ONE observation. Drop a trailing " and …" second clause, cap at 9 words.
  const clampHeadline = (text) => {
    if (!text || typeof text !== 'string') return text;
    let t = text.trim();
    // A headline is price + ONE observation — cut any second clause joined by " and ".
    const andIdx = t.toLowerCase().indexOf(' and ');
    if (andIdx > 0 && t.slice(0, andIdx).trim().split(/\s+/).length >= 4) {
      t = t.slice(0, andIdx).trim();
    }
    const words = t.split(/\s+/);
    if (words.length > 11) t = words.slice(0, 11).join(' ');
    return t.replace(/[,;:\s]+$/, '');
  };

  const displayHeadline = clampHeadline(fixAIText(
    bitcoinPrice && headline
      ? headline.replace(/\$[\d,]+(\.\d{1,2})?[BMTK]?/, `$${parseFloat(bitcoinPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      : headline
  ));

  // Override ALL AI section highlights with real data from metrics (single source of truth)
  const realHighlights = {
    market: [
      m?.price?.change30d != null ? `${m.price.change30d >= 0 ? 'Up' : 'Down'} ${fmt.pct(Math.abs(m.price.change30d))} (30d)` : null,
      athDistance != null ? `${fmt.pct(Math.abs(athDistance))} below ATH` : null,
    ].filter(Boolean).join(', '),
    network: [
      m?.network?.hashrate ? `${fmt.hashrate(m.network.hashrate)} hashrate` : null,
      insight?.analytics?.flags?.networkHealth ? formatKeyForDisplay(insight.analytics.flags.networkHealth).toLowerCase() : null,
    ].filter(Boolean).join(', '),
    mining: [
      insight?.analytics?.benchmarks?.fees ? `Fees ${insight.analytics.benchmarks.fees.level.toLowerCase()}` : null,
      insight?.analytics?.benchmarks?.fees?.context || null,
      // Fallback: fee/subsidy ratio from metrics if benchmarks unavailable
      !insight?.analytics?.benchmarks?.fees && m?.mining?.feeSubsidyRatio != null
        ? `Fee/subsidy ratio: ${fmt.pct(m.mining.feeSubsidyRatio, 2)}`
        : null,
      // Last resort fallback — derive from metrics dynamically
      !insight?.analytics?.benchmarks?.fees && m?.mining?.feeSubsidyRatio == null
        ? `${m?.mining?.currentReward ? (m.mining.currentReward / 1e8).toFixed(4) : '3.125'} BTC subsidy${m?.mining?.currentEpoch ? ` (Epoch ${m.mining.currentEpoch})` : ''}`
        : null,
    ].filter(Boolean).join(', '),
    lightning: [
      m?.lightning?.channels ? `${fmt.num(m.lightning.channels)} channels` : null,
      m?.lightning?.capacity ? `${fmt.btcShort(m.lightning.capacity)} BTC capacity` : null,
    ].filter(Boolean).join(', '),
    mempool: [
      m?.activity?.mempoolTxCount ? `${fmt.num(m.activity.mempoolTxCount)} unconfirmed txs` : null,
      m?.activity?.mempoolUsage && m?.activity?.mempoolMaxSize
        ? `${((m.activity.mempoolUsage / m.activity.mempoolMaxSize) * 100).toFixed(0)}% full`
        : null,
    ].filter(Boolean).join(', '),
  };

  // Tailor to the filter set the insight was generated with — same layout, focused count.
  const activeCategories = Array.isArray(categories) && categories.length > 0 ? categories : null;
  const filteredSections = sections
    ? Object.entries(sections)
        .filter(([key]) => !activeCategories || activeCategories.includes(key))
        .map(([key, sectionData]) =>
          realHighlights[key]
            ? [key, { ...sectionData, highlight: realHighlights[key] }]
            : [key, { ...sectionData, highlight: fixAIText(sectionData.highlight) }]
        )
    : [];

  return (
    <VStack align="stretch" spacing={3}>
      {/* Headline Row */}
      <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
        {displayHeadline && (
          <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 60%' }}>
            <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2} mb={2}>
              <Text fontSize={{ base: '18px', md: '22px' }} fontWeight="700" lineHeight="1.3" color={textPrimary} flex={1}>
                {displayHeadline}
              </Text>
              {displaySentiment && (
                <StatusBadge status={displaySentiment} colorMode={colorMode} />
              )}
            </HStack>
            {(marketScore || athDistance != null) && (
              <HStack spacing={4} mb={alerts?.length > 0 ? 3 : 0} flexWrap="wrap">
                {marketScore && (
                  <HStack spacing={2}>
                    <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}>Market Score:</Text>
                    <Text fontSize="14px" fontWeight="700" color={scoreColor}>{marketScore}/10</Text>
                    <Tooltip label="Considers: price trend, ATH distance, hashrate, fees, Lightning, mempool" placement="bottom" hasArrow bg={colorMode === 'dark' ? THEME_COLORS.primary[500] : '#f5f5f5'} color={textPrimary}>
                      <Box as="span" cursor="help" ml={1}><FontAwesomeIcon icon={faInfoCircle} size="xs" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} /></Box>
                    </Tooltip>
                  </HStack>
                )}
                {athDistance != null && (
                  <HStack spacing={2}>
                    <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}>From ATH:</Text>
                    <Text fontSize="14px" fontWeight="700" color={athDistance >= 0 ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>{athDistance >= 0 ? '+' : ''}{clean(athDistance)}%</Text>
                  </HStack>
                )}
              </HStack>
            )}
            {alerts && alerts.length > 0 && <Flex flexWrap="wrap" gap={2}>{alerts.map((alert, idx) => <AlertBadge key={idx} alert={{ ...alert, message: fixAIText(alert.message) }} colorMode={colorMode} />)}</Flex>}
          </Box>
        )}
        {data && (
          <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 40%' }}>
            <MiniPriceChart data={data} colorMode={colorMode} canonicalChange30d={m?.price?.change30d} />
          </Box>
        )}
      </Flex>

      {/* Section Status Cards */}
      {filteredSections.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: filteredSections.length }} spacing={2}>
          {filteredSections.map(([key, sectionData]) => (
            <SectionStatus key={key} category={key} status={sectionData.status} highlight={sectionData.highlight} colorMode={colorMode} />
          ))}
        </SimpleGrid>
      )}

      {/* Analysis & Outlook */}
      {(textInsights?.length > 0 || outlook) && (
        <Flex flexWrap="wrap" gap={3}>
          {textInsights && textInsights.length > 0 && (
            <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 calc(60% - 6px)' }} minW="250px">
              <Text fontSize="12px" color={labelColor} fontWeight="700" textTransform="uppercase" mb={3}>Analysis</Text>
              <VStack align="stretch" spacing={2}>
                {textInsights.slice(0, 4).map((text, idx) => (
                  <HStack key={idx} align="flex-start" spacing={2}>
                    <Text color={THEME_COLORS.orange[500]} fontSize="13px" mt="1px">•</Text>
                    <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{clampWords(fixAIText(text), 15)}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
          {outlook && (
            <Box bg={colorMode === 'dark' ? '#3d2800' : THEME_COLORS.orange[100]} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 calc(40% - 6px)' }} minW="180px">
              <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase" mb={2}>Watch</Text>
              {Array.isArray(outlook) ? (
                <VStack align="stretch" spacing={2}>
                  {outlook.slice(0, 3).map((item, idx) => (
                    <HStack key={idx} align="flex-start" spacing={2}>
                      <Text color={THEME_COLORS.orange[500]} fontSize="13px" mt="1px">•</Text>
                      <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{clampWords(fixAIText(item), 10)}</Text>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{clampWords(fixAIText(outlook), 10)}</Text>
              )}
            </Box>
          )}
        </Flex>
      )}
    </VStack>
  );
});
UnifiedDashboard.displayName = 'UnifiedDashboard';

// Quick Stats - uses theme colors, compact grid matching app design
const QuickStats = ({ metrics, colorMode, categories }) => {
  if (!metrics) return null;

  const active = Array.isArray(categories) && categories.length > 0 ? categories : null;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const quickMetrics = [
    { label: 'Block Height', value: metrics.network?.blockHeight ? fmt.num(metrics.network.blockHeight) : null },
    { label: 'Hashrate', value: metrics.network?.hashrate ? fmt.hashrate(metrics.network.hashrate) : null },
    { label: 'Difficulty', value: metrics.network?.difficulty ? fmt.difficulty(metrics.network.difficulty) : null },
    { label: 'Mempool', value: metrics.activity?.mempoolTxCount ? `${fmt.num(metrics.activity.mempoolTxCount)} TX` : null },
    { label: 'Fastest Fee', value: metrics.mining?.feeRates?.fastestFee ? `${metrics.mining.feeRates.fastestFee} sat/vB` : null },
    { label: 'Txs 24h', value: metrics.activity?.transactions24h ? formatLargeNumber(metrics.activity.transactions24h, 1) : null },
    { label: 'Active Addrs', value: metrics.activity?.activeAddresses ? formatLargeNumber(metrics.activity.activeAddresses, 1) : null },
    { label: 'LN Nodes', value: metrics.lightning?.nodes ? fmt.num(metrics.lightning.nodes) : null },
    { label: 'LN BTC Capacity', value: metrics.lightning?.capacity ? fmt.btcShort(metrics.lightning.capacity) : null },
    { label: 'Halving', value: metrics.mining?.halvingProgress ? `${metrics.mining.halvingProgress}%` : null }
  ].filter(m => m.value && keyInCategories(m.label, active));

  if (quickMetrics.length === 0) return null;

  return (
    <Box mt={4}>
      <Text fontSize="13px" color={labelColor} mb={2} fontWeight="700" textTransform="uppercase">Key Metrics</Text>
      <Flex flexWrap="wrap" gap={2}>
        {quickMetrics.map((m, idx) => (
          <Box key={idx} flex={{ base: '1 1 calc(50% - 4px)', md: '1 1 calc(20% - 6.4px)' }} minW="120px">
            <MetricCard metric={{ ...m, significance: 'low' }} colorMode={colorMode} />
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

// Main Component
export const AIInsightsButton = ({
  data,
  bitcoinPrice,
  lastMempoolBlockData,
  fees,
  da,
  mempoolInfo,
  diamondbackData,
  blockMetrics
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure();
  const { colorMode } = useColorMode();

  const [selectedCategories, setSelectedCategories] = useState(loadSelectedCategories);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [cachedBlockHeight, setCachedBlockHeight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLog, setLoadingLog] = useState([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [providerTick, setProviderTick] = useState(0); // bump to re-read provider config after changes
  const [showIntro, setShowIntro] = useState(false);
  const [shareState, setShareState] = useState('idle'); // idle | busy | done
  const loadStartRef = useRef(null);
  const abortControllerRef = useRef(null);

  const dismissIntro = () => {
    setShowIntro(false);
    localStorage.setItem(INTRO_KEY, String(Date.now()));
  };

  // Progress callback from generateStructuredInsight
  const handleProgress = useCallback(({ step, detail }) => {
    setLoadingLog(prev => {
      if (prev.length > 0 && prev[prev.length - 1].step === step) return prev;
      return [...prev, { step, detail, ts: Date.now() }];
    });
  }, []);

  // Elapsed timer while loading
  useEffect(() => {
    if (!loading) { setElapsedMs(0); return; }
    loadStartRef.current = Date.now();
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - loadStartRef.current);
    }, 100);
    return () => clearInterval(timer);
  }, [loading]);

  // Restore most recent cached insight on mount (regardless of block/categories)
  // Staleness is surfaced via the footer indicator, not by hiding the insight.
  useEffect(() => {
    const map = loadCacheMap();
    const entries = Object.entries(map).sort(([, a], [, b]) => (b?.timestamp || 0) - (a?.timestamp || 0));
    if (entries.length > 0 && entries[0][1]?.insight) {
      const entry = entries[0][1];
      setCurrentInsight(entry.insight);
      setCachedBlockHeight(entry.blockHeight ?? null);
    }
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Persist category selection across sessions
  useEffect(() => {
    try {
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(selectedCategories));
    } catch (e) {
      console.warn('[AIInsights] Failed to save categories:', e.message);
    }
  }, [selectedCategories]);

  const bgColor = colorMode === 'dark' ? '#121212' : '#f5f5f5';
  const secondaryText = colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300];

  const updateProviderInfo = () => {
    setProviderTick(t => t + 1);
  };

  const getAllData = useCallback(() => ({
    ...data,
    realtime: {
      bitcoinPrice,
      lastMempoolBlockData,
      fees,
      da,
      mempoolInfo,
      diamondbackData,
      blockMetrics
    }
  }), [data, bitcoinPrice, lastMempoolBlockData, fees, da, mempoolInfo, diamondbackData, blockMetrics]);

  useEffect(() => {
    if (data) {
      const allData = getAllData();
      setMetrics(getMetricsSummary(allData));
    }
  }, [data, getAllData]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTopVisible(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const bottomPosition = scrollToTopVisible ? '8rem' : '4.5rem';

  const handleButtonClick = () => {
    // Works out of the box — the data-driven engine runs with no key.
    // The gear icon (in the modal) is the path to add an optional AI provider.
    updateProviderInfo();
    if (shouldShowIntro()) setShowIntro(true);
    onOpen();
  };

  const buildInsight = (result) => ({
    structured: result.type === 'structured',
    data: result.data,
    provider: result.provider,
    model: result.model,
    usedFallback: result.usedFallback,
    fallbackFrom: result.fallbackFrom || null,
    analytics: result.analytics,
    categories: [...selectedCategories],
    isLocal: result.strategy === 'local' || result.provider === 'Timechain Stats AI'
  });

  const commitInsight = (newInsight) => {
    const blockHeight = lastMempoolBlockData?.height ?? null;
    const currency = (data?.selectedCurrency || 'usd').toLowerCase();
    setCurrentInsight(newInsight);
    setCachedBlockHeight(blockHeight);
    saveCacheEntry(makeCacheKey(blockHeight, selectedCategories, currency), {
      insight: newInsight,
      blockHeight,
      timestamp: Date.now()
    });
  };

  // Run the free Timechain Stats AI engine (with the ~5s staged "thinking" pass).
  // When `fallbackFrom` is set (a provider name), it runs as a marked fallback after
  // that provider failed — quicker, and flagged so the UI shows "(fallback)".
  const runLocalInsight = async (fallbackFrom = null) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setError(null);
    setLoading(true);
    setLoadingLog([]);
    try {
      if (fallbackFrom) {
        handleProgress({ step: 'fallback', detail: `${fallbackFrom} unavailable, falling back to Timechain Stats AI` });
        await sleep(700);
      }
      for (const s of LOCAL_THINKING_STEPS) {
        if (controller.signal.aborted) return;
        handleProgress(s);
        await sleep(fallbackFrom ? 500 : 1000); // ~5s normally; quicker after a failed provider
      }
      if (controller.signal.aborted) return;
      const localResult = generateLocalInsight(getAllData(), selectedCategories);
      if (fallbackFrom) {
        localResult.usedFallback = true;
        localResult.fallbackFrom = fallbackFrom;
      }
      commitInsight(buildInsight(localResult));
    } catch (err) {
      console.warn('[AIInsights] Local engine failed:', err.message);
      setError('Could not build insights from current data.');
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const fetchInsight = async () => {
    if (!data) {
      setError('No data available. Please wait for data to load.');
      return;
    }

    // A specific AI provider is selected but its key/endpoint is missing → prompt for it.
    const selectedId = getActiveProvider();
    if (selectedId && selectedId !== NO_AI) {
      const stored = getProviderKey(selectedId);
      const ready = isConfigValid({
        provider: selectedId,
        apiKey: stored?.apiKey || null,
        endpoint: stored?.endpoint || null
      });
      if (!ready) {
        onConfigOpen();
        return;
      }
    }

    // Timechain Stats AI (local): selected NO_AI, or no provider selected/valid.
    if (!shouldUseAI()) {
      await runLocalInsight();
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setLoadingLog([]);
    setError(null);

    try {
      const allData = getAllData();
      const result = await generateStructuredInsight('overview', allData, selectedCategories, handleProgress, controller.signal);
      commitInsight(buildInsight(result));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('[AIInsights] Generation failed:', err.message);
        // Don't dead-end: fall back to the free engine, but clearly MARK it as a
        // fallback (footer + loading log) so it's never mistaken for the selection.
        const failedName = selectedId && selectedId !== NO_AI
          ? (AI_PROVIDERS[selectedId]?.name || selectedId)
          : 'AI provider';
        await runLocalInsight(failedName);
      }
    } finally {
      // Only clear loading if this controller is still the active request;
      // a newer fetchInsight call may have replaced it and own the loading state.
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  // Return to the generate screen so the user can adjust filters before the next run.
  // The prior insight stays cached in localStorage — nothing is lost.
  const handleRefresh = () => {
    setError(null);
    setCurrentInsight(null);
  };

  const handleShare = useCallback(async () => {
    const insight = currentInsight?.data;
    if (!insight || shareState === 'busy') return;
    setShareState('busy');
    try {
      const sections = Object.entries(insight.sections || {})
        .filter(([key]) => CATEGORIES[key])
        .map(([key, s]) => ({
          label: CATEGORIES[key].label,
          status: s.status,
          color: statusHex(s.status)
        }));

      const stats = [
        { label: 'Price', value: bitcoinPrice ? `$${Math.round(parseFloat(bitcoinPrice)).toLocaleString()}` : null },
        { label: 'Hashrate', value: metrics?.network?.hashrate ? fmt.hashrate(metrics.network.hashrate) : null },
        { label: 'Block', value: metrics?.network?.blockHeight ? `#${fmt.num(metrics.network.blockHeight)}` : null },
        { label: 'Fastest Fee', value: metrics?.mining?.feeRates?.fastestFee ? `${metrics.mining.feeRates.fastestFee} sat/vB` : null }
      ];

      await shareInsightCard({
        headline: insight.headline,
        sentiment: insight.overallSentiment || insight.sentiment,
        marketScore: insight.marketScore,
        sections,
        stats,
        blockHeight: lastMempoolBlockData?.height ?? cachedBlockHeight,
        dateStr: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        provider: currentInsight?.provider
      });
      setShareState('done');
      setTimeout(() => setShareState('idle'), 2000);
    } catch (err) {
      console.warn('[AIInsights] Share failed:', err.message);
      setShareState('idle');
    }
  }, [currentInsight, shareState, bitcoinPrice, metrics, lastMempoolBlockData, cachedBlockHeight]);

  const toggleCategory = (key) => {
    setSelectedCategories(prev => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleConfigured = () => {
    updateProviderInfo();
    onOpen();
  };

  const handleModalClose = useCallback(() => {
    abortControllerRef.current?.abort();
    onClose();
  }, [onClose]);

  // What the user has SELECTED (not what would run as fallback). providerTick forces a re-read after config edits.
  const selectedProviderId = providerTick >= 0 ? getActiveProvider() : null;
  const usingAiProvider = !!selectedProviderId && selectedProviderId !== NO_AI;
  const selectedProviderName = usingAiProvider
    ? (AI_PROVIDERS[selectedProviderId]?.name || selectedProviderId)
    : 'Timechain Stats AI';
  const selectedStored = usingAiProvider ? getProviderKey(selectedProviderId) : null;
  const selectedModelLabel = usingAiProvider
    ? (selectedStored?.model && selectedStored.model !== AUTO_MODEL ? selectedStored.model : 'Auto · cheapest model')
    : 'Free · built-in';
  const providerCount = getConfiguredCount();
  const estTokens = getEstimatedTokens(selectedCategories);
  const currentBlockHeight = lastMempoolBlockData?.height ?? null;
  const blocksStale = cachedBlockHeight != null && currentBlockHeight != null && currentBlockHeight > cachedBlockHeight
    ? currentBlockHeight - cachedBlockHeight
    : 0;

  return (
    <>
      <MotionBox
        position="fixed"
        bottom={bottomPosition}
        right="1.2rem"
        zIndex={99}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut', delay: 1 }}
        style={{ transition: 'bottom 0.3s ease' }}
      >
        <Tooltip label="AI Insights" placement="left">
          <IconButton
            onClick={handleButtonClick}
            aria-label="AI Insights"
            icon={<FontAwesomeIcon icon={faRobot} />}
            bg="#8B00FF"
            color="#ffffff"
            _hover={{ bg: "#ba68c8" }}
            size="md"
            borderRadius="3px"
          />
        </Tooltip>
      </MotionBox>

      <AIConfigModal
        isOpen={isConfigOpen}
        onClose={onConfigClose}
        onConfigured={handleConfigured}
      />

      <Modal isOpen={isOpen} onClose={handleModalClose} size="full" scrollBehavior="inside">
        <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.95)" />
        <ModalContent
          bg={bgColor}
          borderRadius="0"
          m={0}
          maxH="100vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            borderBottomWidth="1px"
            borderColor={colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
            py={3}
            flexShrink={0}
          >
            <Flex justify="space-between" align="center" pr={8} flexWrap="wrap" gap={2}>
              <HStack spacing={3}>
                <HStack spacing="5px">
                  <Box bg={THEME_COLORS.gray[500]} w="32px" h="32px" borderRadius="3px" />
                  <Box bg={THEME_COLORS.gray[500]} w="32px" h="32px" borderRadius="3px" />
                  <Box
                    bg={THEME_COLORS.orange[500]}
                    p={2}
                    borderRadius="3px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FontAwesomeIcon icon={faRobot} color="#ffffff" size="lg" />
                  </Box>
                </HStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="18px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>Timechain Stats AI</Text>
                  <Text fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500]} textTransform="uppercase">Bitcoin Analysis</Text>
                </VStack>
              </HStack>
              <HStack spacing={2}>
                {SHOW_SHARE && currentInsight && !loading && (
                  <Tooltip label={shareState === 'done' ? 'Saved!' : 'Share as image'}>
                    <IconButton
                      aria-label="Share insight"
                      icon={<FontAwesomeIcon icon={shareState === 'done' ? faCheckCircle : faShareNodes} />}
                      size="sm"
                      variant="ghost"
                      onClick={handleShare}
                      isLoading={shareState === 'busy'}
                      color={shareState === 'done' ? THEME_COLORS.green[500] : (colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300])}
                      _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                    />
                  </Tooltip>
                )}
                <Tooltip label="Refresh analysis">
                  <IconButton
                    aria-label="Refresh"
                    icon={<FontAwesomeIcon icon={faSync} spin={loading} />}
                    size="sm"
                    variant="ghost"
                    onClick={handleRefresh}
                    isDisabled={loading}
                    color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
                    _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                  />
                </Tooltip>
                <Tooltip label="AI provider settings">
                  <IconButton
                    aria-label="Settings"
                    icon={<FontAwesomeIcon icon={faCog} />}
                    size="sm"
                    variant="ghost"
                    onClick={onConfigOpen}
                    color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
                    _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton
            color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
            _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
          />
          <ModalBody py={4} px={{ base: 4, md: 6, lg: 8 }} flex={1} overflow="auto">
            <Flex
              direction={{ base: 'column', xl: 'row' }}
              gap={4}
              maxW="1800px"
              mx="auto"
              h="100%"
            >
              {/* Main Content */}
              <Box flex={1} minW={0} display="flex" flexDirection="column">

                {/* Loading State — Cypherpunk Terminal */}
                {loading && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={6}>
                    <Box
                      w="100%" maxW="680px"
                      bg={colorMode === 'dark' ? '#0a0e0a' : '#1a1a2e'}
                      border="1px solid"
                      borderColor={colorMode === 'dark' ? '#2a5a2a' : '#2a2a4e'}
                      borderRadius="6px"
                      overflow="hidden"
                      fontFamily="'Courier New', Courier, monospace"
                    >
                      {/* Terminal header bar */}
                      <HStack
                        px={4} py={2}
                        bg={colorMode === 'dark' ? '#0f1a0f' : '#12122e'}
                        borderBottom="1px solid"
                        borderColor={colorMode === 'dark' ? '#2a5a2a' : '#2a2a4e'}
                        justify="space-between"
                      >
                        <HStack spacing={1.5}>
                          <Box w="10px" h="10px" borderRadius="full" bg="#ff5f56" />
                          <Box w="10px" h="10px" borderRadius="full" bg="#ffbd2e" />
                          <Box w="10px" h="10px" borderRadius="full" bg="#27c93f" />
                        </HStack>
                        <Text fontSize="12px" color="#8ac88a" letterSpacing="1px" fontWeight="600">
                          satoshi@timechain ~
                        </Text>
                        <Text fontSize="11px" color="#6aaa6a" fontWeight="600">
                          {(elapsedMs / 1000).toFixed(1)}s
                        </Text>
                      </HStack>

                      {/* Terminal body */}
                      <Box px={5} py={5} minH="240px">
                        {/* Static init line */}
                        <Text fontSize="13px" color="#6aaa6a" mb={2}>
                          $ ./analyze-timechain --depth=full
                        </Text>

                        {/* Real progress log lines */}
                        {loadingLog.map((entry, i) => (
                          <HStack key={i} spacing={2} mb={1} align="baseline">
                            <Text fontSize="11px" color="#6aaa6a" flexShrink={0}>
                              [{entry.ts ? new Date(entry.ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '...'}]
                            </Text>
                            <Text fontSize="13px" color={i === loadingLog.length - 1 ? '#7aff7a' : '#8ac88a'}>
                              {entry.detail}
                            </Text>
                          </HStack>
                        ))}

                        {/* Thinking indicator — pulsing dots (clearly "working", not a prompt) */}
                        <HStack spacing={2} mt={3} align="center">
                          <Text fontSize="13px" color="#7aff7a" fontWeight="700">{'>'}</Text>
                          <Text fontSize="13px" color="#7aff7a">thinking</Text>
                          <HStack spacing={1.5} ml={1}>
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                w="7px" h="7px" borderRadius="full" bg="#7aff7a"
                                animation={`tcsThink 1.2s ease-in-out ${i * 0.18}s infinite`}
                                sx={{ '@keyframes tcsThink': { '0%, 80%, 100%': { opacity: 0.25, transform: 'scale(0.7)' }, '40%': { opacity: 1, transform: 'scale(1)' } } }}
                              />
                            ))}
                          </HStack>
                        </HStack>

                        {/* Hex decoration — subtle scrolling hash */}
                        <Text
                          fontSize="10px" color="#5fae5f" mt={5}
                          letterSpacing="1.5px" wordBreak="break-all"
                          opacity={0.55}
                        >
                          {Array.from({ length: 56 }, (_, i) => '0123456789abcdef'[(Math.floor(elapsedMs / 90) + i * 7) % 16]).join('')}
                        </Text>
                      </Box>
                    </Box>

                    {/* Provider info below terminal */}
                    <Text fontSize="13px" color={THEME_COLORS.orange[500]} mt={4} letterSpacing="0.5px" fontWeight="600">
                      Querying {loadingLog.find(l => l.step === 'generating')?.detail?.replace('Querying ', '')?.replace('...', '') || 'AI provider'}
                    </Text>
                  </Flex>
                )}

                {/* Error State */}
                {error && !loading && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={8}>
                    <Text color={THEME_COLORS.red[300]} mb={4} fontSize="14px" whiteSpace="pre-line" textAlign="center">{error}</Text>
                    <Button size="sm" bg={THEME_COLORS.orange[500]} color="#ffffff" _hover={{ bg: THEME_COLORS.orange[300] }} onClick={fetchInsight}>
                      Try Again
                    </Button>
                  </Flex>
                )}

                {/* Insight Dashboard */}
                {currentInsight && !loading && (
                  <VStack align="stretch" spacing={3}>
                    {currentInsight.structured ? (
                      <UnifiedDashboard
                        insight={currentInsight.data}
                        colorMode={colorMode}
                        data={data}
                        bitcoinPrice={bitcoinPrice}
                        metrics={metrics}
                        categories={currentInsight.categories}
                      />
                    ) : (
                      <Box bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'} p={4} borderRadius="3px">
                        <Text fontSize="13px" lineHeight="1.7" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{currentInsight.data}</Text>
                      </Box>
                    )}
                    {/* Advanced Analytics - in main pane */}
                    {currentInsight?.analytics && (
                      <Box>
                        <Text fontSize="11px" color={colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500]} fontWeight="700" textTransform="uppercase" mb={2}>
                          Advanced Analytics
                        </Text>
                        <AnalyticsPanel analytics={currentInsight.analytics} colorMode={colorMode} categories={currentInsight.categories} />
                      </Box>
                    )}
                    {/* QuickStats at bottom - only shows after AI is called */}
                    <QuickStats metrics={metrics} colorMode={colorMode} categories={currentInsight.categories} />
                  </VStack>
                )}

                {/* Generate screen — icon, description, filters, and Analyze all centered together */}
                {!currentInsight && !loading && !error && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={8} gap={7}>
                    <VStack spacing={3} maxW="480px" textAlign="center">
                      <Icon as={FontAwesomeIcon} icon={faRobot} boxSize={10} color={THEME_COLORS.orange[500]} />
                      <Text color={secondaryText} fontSize="14px">
                        Get instant AI-powered analysis of thousands of Bitcoin network datapoints
                      </Text>
                    </VStack>

                    {/* Choose AI provider — opens the provider modal */}
                    <VStack spacing={2} w="100%">
                      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" color={secondaryText} letterSpacing="0.5px">
                        Choose your AI provider
                      </Text>
                      <Button
                        variant="outline"
                        size="md"
                        onClick={onConfigOpen}
                        color={colorMode === 'dark' ? '#ffffff' : '#121212'}
                        borderColor={colorMode === 'dark' ? THEME_COLORS.gray[500] : THEME_COLORS.gray[200]}
                        fontWeight="600"
                        leftIcon={<FontAwesomeIcon icon={faCog} />}
                        rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                        _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}
                      >
                        {usingAiProvider ? `${selectedProviderName} · ${selectedModelLabel}` : selectedProviderName}
                      </Button>
                    </VStack>

                    {/* Focus filters — which categories this run covers */}
                    <VStack spacing={2} w="100%">
                      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" color={secondaryText} letterSpacing="0.5px">
                        Focus your analysis
                      </Text>
                      <Flex wrap="wrap" gap={2} justify="center" maxW="560px">
                        {ALL_CATEGORIES.map(key => {
                          const cat = CATEGORIES[key];
                          const isActive = selectedCategories.includes(key);
                          const inactiveBorder = colorMode === 'dark' ? THEME_COLORS.gray[500] : THEME_COLORS.gray[200];
                          const inactiveColor = colorMode === 'dark' ? THEME_COLORS.gray[100] : THEME_COLORS.gray[500];
                          return (
                            <Button
                              key={key}
                              size="sm"
                              bg={isActive ? THEME_COLORS.gray[500] : 'transparent'}
                              color={isActive ? '#ffffff' : inactiveColor}
                              borderWidth="1px"
                              borderColor={isActive ? THEME_COLORS.gray[500] : inactiveBorder}
                              borderRadius="3px"
                              leftIcon={<FontAwesomeIcon icon={cat.icon} />}
                              onClick={() => toggleCategory(key)}
                              _hover={{ bg: isActive ? THEME_COLORS.gray[300] : (colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)') }}
                            >
                              {cat.label}
                            </Button>
                          );
                        })}
                      </Flex>
                    </VStack>

                    {/* Analyze */}
                    <VStack spacing={2}>
                      <Button
                        bg={THEME_COLORS.orange[500]}
                        color="#ffffff"
                        _hover={{ bg: THEME_COLORS.orange[300] }}
                        size="lg"
                        onClick={fetchInsight}
                        leftIcon={<FontAwesomeIcon icon={faLightbulb} />}
                      >
                        Analyze Network
                      </Button>
                      <Text fontSize="10px" color={secondaryText} textAlign="center">
                        ~{estTokens.toLocaleString()} tokens
                      </Text>
                    </VStack>
                  </Flex>
                )}
              </Box>


              {/* Sidebar: Progress Bars + Flair — only shown alongside a generated dashboard */}
              {currentInsight && !loading && (
                <Box w={{ base: '100%', xl: '340px' }} flexShrink={0}>
                  <VStack spacing={3} align="stretch">
                    {metrics?.progressBars?.difficulty && (
                      <InsightProgressBar label="Difficulty Adj" progress={metrics.progressBars.difficulty.progress} detail={metrics.progressBars.difficulty.detail} colorMode={colorMode} />
                    )}
                    {metrics?.progressBars?.halving && (
                      <InsightProgressBar label="Next Halving" progress={metrics.progressBars.halving.progress} detail={metrics.progressBars.halving.detail} colorMode={colorMode} />
                    )}
                    <SatoshiQuoteFlair colorMode={colorMode} />
                    <LexiconTermFlair colorMode={colorMode} />
                  </VStack>
                </Box>
              )}
            </Flex>
          </ModalBody>

          {/* Provider Info Footer */}
          <HStack
            px={{ base: 4, md: 6, lg: 8 }}
            py={2}
            borderTopWidth="1px"
            borderColor={colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
            fontSize="11px"
            color={secondaryText}
            justify="center"
            spacing={4}
            flexWrap="wrap"
            flexShrink={0}
          >
            {providerCount > 0 && (
              <Text>{providerCount} provider{providerCount !== 1 ? 's' : ''} configured</Text>
            )}
            {currentInsight?.provider ? (
              <>
                {providerCount > 0 && <Text>•</Text>}
                <Text>
                  {currentInsight.model || currentInsight.provider}
                  {!currentInsight.usedFallback && (
                    <Text as="span" color={THEME_COLORS.orange[500]} fontWeight="700"> · Active</Text>
                  )}
                  {currentInsight.usedFallback && currentInsight.isLocal && (
                    <Text as="span" color={THEME_COLORS.gray[300]}> (fallback)</Text>
                  )}
                </Text>
                {currentInsight.isLocal && currentInsight.fallbackFrom && (
                  <>
                    <Text>•</Text>
                    <Text color={THEME_COLORS.orange[700]}>{currentInsight.fallbackFrom} unavailable</Text>
                  </>
                )}
                {!currentInsight.isLocal && (
                  <>
                    <Text>•</Text>
                    <Text>via <Text as="span" color={THEME_COLORS.orange[500]}>{currentInsight.provider}</Text>{currentInsight.usedFallback && <Text as="span" color={THEME_COLORS.gray[300]}> (fallback)</Text>}</Text>
                  </>
                )}
                <Text>•</Text>
                <Text>~{estTokens.toLocaleString()} tokens</Text>
                {blocksStale > 0 && (
                  <>
                    <Text>•</Text>
                    <Text color={THEME_COLORS.orange[700]}>
                      {blocksStale} block{blocksStale !== 1 ? 's' : ''} old
                    </Text>
                  </>
                )}
              </>
            ) : (
              <>
                {providerCount > 0 && <Text>•</Text>}
                <Text>{usingAiProvider ? `${selectedProviderName} · ${selectedModelLabel}` : selectedProviderName}</Text>
                <Text>•</Text>
                <Text>~{estTokens.toLocaleString()} tokens</Text>
              </>
            )}
          </HStack>

          {/* Fine print — pre-release disclaimer */}
          <Text
            px={{ base: 4, md: 6, lg: 8 }}
            pb={2}
            fontSize="10px"
            color={colorMode === 'dark' ? THEME_COLORS.gray[300] : THEME_COLORS.gray[200]}
            textAlign="center"
            lineHeight="1.4"
            flexShrink={0}
          >
            Pre-release preview · for informational purposes only. Not financial advice. Do not rely on this for real financial decisions.
          </Text>
        </ModalContent>
      </Modal>

      {/* Intro onboarding modal — overlays the full-screen AI modal */}
      <Modal isOpen={showIntro} onClose={dismissIntro} size="md" isCentered>
        <ModalOverlay backgroundColor="rgba(0, 0, 0, 0.6)" />
        <ModalContent bg={colorMode === 'dark' ? THEME_COLORS.primary[500] : '#ffffff'} borderRadius="3px">
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              <Box bg={THEME_COLORS.orange[500]} p={2} borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                <FontAwesomeIcon icon={faRobot} color="#ffffff" />
              </Box>
              <Text fontSize="18px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>Timechain Stats AI</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} />
          <ModalBody pt={0} pb={2}>
            <List spacing={3}>
              {[
                { icon: faLightbulb, text: 'Click "Analyze Network" for an instant, data-driven Bitcoin read. No key required.' },
                { icon: faGlobe, text: 'Analysis covers Market, Network, Mining, Lightning, and Mempool' },
                { icon: faCog, text: 'Optional: add your own AI provider (gear icon) for deeper narrative analysis' },
                { icon: faCheckCircle, text: 'You provide your own API key. TimechainStats never sees or stores your keys.' },
              ].map((item, idx) => (
                <ListItem key={idx} display="flex" alignItems="flex-start" fontSize="14px" color={colorMode === 'dark' ? THEME_COLORS.gray[100] : THEME_COLORS.gray[300]}>
                  <ListIcon as={FontAwesomeIcon} icon={item.icon} color={THEME_COLORS.orange[500]} mt="3px" />
                  {item.text}
                </ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button bg={THEME_COLORS.orange[500]} color="#ffffff" _hover={{ bg: THEME_COLORS.orange[300] }} onClick={dismissIntro} w="100%">
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AIInsightsButton;
